/**
 * Gummies landing page behaviour, shared by every `gummies-*` section.
 *
 * - Buy Now buttons: show the price of the bundle picked in the hero, and fall back
 *   to a plain link when the page has no hero form to submit.
 * - Hero: quantity selector stays in sync with the bundle options, thumbnail gallery
 *   and the note under the bundle options.
 * - Videos: poster + play button that starts the video and pauses the others.
 * - Carousels, ship-to tabs, the Supplement Facts dialog and the workout log bars.
 *
 * Everything is wired with delegated listeners on the document, so sections added
 * or re-rendered in the theme editor keep working without re-initialising.
 */

import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

const FORM_ID = 'gm-buy-form';

function buyForm() {
  return /** @type {HTMLFormElement | null} */ (document.getElementById(FORM_ID));
}

/* ---------- Bundle selection, quantity and Buy Now prices ---------- */

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLInputElement | null}
 */
function quantityField(form) {
  return form.querySelector('input[name="quantity"]');
}

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLInputElement[]}
 */
function bundleRadios(form) {
  return /** @type {HTMLInputElement[]} */ ([...form.querySelectorAll('input[name="gm-bundle"]')]);
}

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLInputElement | null}
 */
function selectedBundle(form) {
  return form.querySelector('input[name="gm-bundle"]:checked');
}

/**
 * @param {HTMLFormElement} form
 * @param {number} qty
 */
function setQuantityValue(form, qty) {
  const value = String(Math.max(1, qty));
  const selector = form.querySelector('quantity-selector-component');
  const input = quantityField(form);
  if (selector && 'setValue' in selector && typeof selector.setValue === 'function') {
    selector.setValue(value);
  } else if (input) {
    input.value = value;
  }
  if (selector && 'updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
    selector.updateButtonStates();
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {number} qty
 * @returns {HTMLInputElement | null}
 */
function bundleForQuantity(form, qty) {
  return bundleRadios(form).find((radio) => Number(radio.dataset.quantity) === qty) ?? null;
}

/**
 * @param {HTMLFormElement} form
 * @param {number} qty
 * @returns {string}
 */
function priceLabelForQuantity(form, qty) {
  const match = bundleForQuantity(form, qty);
  if (match?.dataset.priceLabel) return match.dataset.priceLabel;

  const unit = Number(form.dataset.gmUnit);
  if (!Number.isFinite(unit) || unit <= 0) return '';

  const eligible = bundleRadios(form)
    .filter((radio) => Number(radio.dataset.quantity) <= qty)
    .sort((a, b) => Number(b.dataset.quantity) - Number(a.dataset.quantity))[0];
  const discount = Number(eligible?.dataset.discount) || 0;
  const linePrice = unit * qty;
  const total = linePrice - Math.round((linePrice * discount) / 100);
  return formatMoney(total, form.dataset.gmMoneyFormat || '${{amount}}', form.dataset.gmCurrency || '');
}

/**
 * @param {HTMLFormElement} form
 * @param {number} qty
 */
function applyPrice(form, qty) {
  const label = priceLabelForQuantity(form, qty);
  if (!label) return;
  for (const el of document.querySelectorAll('[data-gm-price]')) {
    el.textContent = label;
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLInputElement | null} bundle
 */
function applyNote(form, bundle) {
  const scope = form.closest('.gm-hero') ?? form;
  const noteId = bundle?.dataset.note ?? '';
  for (const note of scope.querySelectorAll('[data-gm-note]')) {
    /** @type {HTMLElement} */ (note).hidden = note.getAttribute('data-gm-note') !== noteId;
  }
}

/**
 * Applies the selected bundle: its quantity goes into the selector, its price onto
 * every Buy Now button, and its note under the options.
 */
function syncBundle() {
  const form = buyForm();
  if (!form) return;

  const checked = selectedBundle(form);
  if (checked?.dataset.quantity) setQuantityValue(form, Number(checked.dataset.quantity));

  const qty = Number(quantityField(form)?.value) || Number(checked?.dataset.quantity) || 1;
  applyPrice(form, qty);
  applyNote(form, checked);
}

/**
 * Mirrors a quantity-selector change onto the matching bundle card (or clears the
 * selection when no card matches) and refreshes the Buy Now prices.
 * @param {number} qty
 */
function syncQuantity(qty) {
  const form = buyForm();
  if (!form || !Number.isFinite(qty) || qty < 1) return;

  const match = bundleForQuantity(form, qty);
  for (const radio of bundleRadios(form)) radio.checked = radio === match;

  applyPrice(form, qty);
  applyNote(form, match);
}

document.addEventListener('change', (event) => {
  const target = event.target;
  // Compare the form element itself rather than `form.id`, which a form field named
  // "id" would shadow.
  if (!(target instanceof HTMLInputElement) || !target.form || target.form !== buyForm()) return;
  if (target.name === 'gm-bundle') syncBundle();
  if (target.name === 'quantity') syncQuantity(Number(target.value));
});

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const form = buyForm();
  const target = event.target;
  if (!form || !(target instanceof Node) || !form.contains(target)) return;
  const qty = Number(/** @type {{ detail?: { quantity?: number } }} */ (event).detail?.quantity);
  if (Number.isFinite(qty)) syncQuantity(qty);
});

document.addEventListener('click', (event) => {
  const button = /** @type {HTMLElement | null} */ (
    event.target instanceof Element ? event.target.closest('[data-gm-buy]') : null
  );
  if (!button || buyForm()) return;

  // No hero on the page: send the shopper to the product instead.
  const url = button.dataset.fallbackUrl;
  if (url) {
    event.preventDefault();
    window.location.href = url;
  }
});

/* ---------- Hero gallery ---------- */

const GALLERY_DRAG_THRESHOLD = 12;
const GALLERY_SWIPE_THRESHOLD = 48;
const boundGalleries = new WeakSet();

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isDesktopGallery() {
  return window.matchMedia('(min-width: 990px)').matches;
}

function galleryThumbs(gallery) {
  return [...gallery.querySelectorAll('[data-gm-thumb]')];
}

function currentGalleryIndex(gallery) {
  const active = gallery.querySelector('[data-gm-slide][data-active="true"]');
  return Number(active?.getAttribute('data-gm-slide') || 0);
}

/**
 * @param {HTMLElement} rail
 * @param {HTMLElement} thumb
 * @param {'auto' | 'smooth'} [behavior]
 */
function scrollThumbIntoRail(rail, thumb, behavior) {
  const vertical = isDesktopGallery();
  const railBox = rail.getBoundingClientRect();
  const thumbBox = thumb.getBoundingClientRect();
  const motion = behavior ?? (prefersReducedMotion() ? 'auto' : 'smooth');

  if (vertical) {
    if (thumbBox.top < railBox.top) rail.scrollBy({ top: thumbBox.top - railBox.top, behavior: motion });
    else if (thumbBox.bottom > railBox.bottom) rail.scrollBy({ top: thumbBox.bottom - railBox.bottom, behavior: motion });
    return;
  }

  if (thumbBox.left < railBox.left) rail.scrollBy({ left: thumbBox.left - railBox.left, behavior: motion });
  else if (thumbBox.right > railBox.right) rail.scrollBy({ left: thumbBox.right - railBox.right, behavior: motion });
}

function activateGallerySlide(gallery, index) {
  const slides = [...gallery.querySelectorAll('[data-gm-slide]')];
  const thumbs = galleryThumbs(gallery);
  if (!slides.length) return;

  const next = ((Number(index) % slides.length) + slides.length) % slides.length;
  const key = String(next);

  for (const slide of slides) {
    slide.setAttribute('data-active', String(slide.getAttribute('data-gm-slide') === key));
  }
  for (const thumb of thumbs) {
    if (thumb.getAttribute('data-gm-thumb') === key) thumb.setAttribute('aria-current', 'true');
    else thumb.removeAttribute('aria-current');
  }

  const rail = gallery.querySelector('[data-gm-thumbs]');
  const activeThumb = thumbs[next];
  if (rail instanceof HTMLElement && activeThumb instanceof HTMLElement) {
    scrollThumbIntoRail(rail, activeThumb);
  }
}

/**
 * When the clicked thumb is the first or last fully visible item, and another
 * product image sits past that edge, the rail steps one thumb in that direction.
 * @param {HTMLElement} rail
 * @param {Element} thumb
 */
function nudgeThumbRail(rail, thumb) {
  const thumbs = [...rail.querySelectorAll('[data-gm-thumb]')];
  const current = thumbs.indexOf(thumb);
  const prev = thumbs[current - 1];
  const next = thumbs[current + 1];

  const vertical = isDesktopGallery();
  const railBox = rail.getBoundingClientRect();
  const thumbBox = thumb.getBoundingClientRect();
  const edge = 12;
  const firstVisible = vertical
    ? thumbBox.top <= railBox.top + edge
    : thumbBox.left <= railBox.left + edge;
  const lastVisible = vertical
    ? thumbBox.bottom >= railBox.bottom - edge
    : thumbBox.right >= railBox.right - edge;

  const styles = getComputedStyle(rail);
  const gap = parseFloat(vertical ? styles.rowGap || styles.gap : styles.columnGap || styles.gap) || 0;
  const step = (vertical ? thumbBox.height : thumbBox.width) + gap;
  const behavior = prefersReducedMotion() ? 'auto' : 'smooth';

  if (prev instanceof HTMLElement && firstVisible) {
    rail.scrollBy({
      top: vertical ? -step : 0,
      left: vertical ? 0 : -step,
      behavior,
    });
    return;
  }

  if (next instanceof HTMLElement && lastVisible) {
    rail.scrollBy({
      top: vertical ? step : 0,
      left: vertical ? 0 : step,
      behavior,
    });
  }
}

/**
 * @param {Element} thumb
 */
function selectHeroThumb(thumb) {
  const gallery = thumb.closest('[data-gm-gallery]');
  if (!gallery) return;
  const rail = gallery.querySelector('[data-gm-thumbs]');
  activateGallerySlide(gallery, thumb.getAttribute('data-gm-thumb'));
  if (rail instanceof HTMLElement) nudgeThumbRail(rail, thumb);
}

/**
 * Mouse/pen drag-to-scroll on the thumbnail rail. Capture starts only after the
 * pointer actually moves, so a plain click still selects the thumbnail.
 * Touch keeps native overflow and uses the click handler.
 * @param {HTMLElement} rail
 */
function bindThumbRailDrag(rail) {
  let pointerId = null;
  let originX = 0;
  let originY = 0;
  let originScroll = 0;
  let vertical = false;
  let dragging = false;

  rail.addEventListener('dragstart', (event) => event.preventDefault());

  rail.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 || event.pointerType === 'touch') return;
    pointerId = event.pointerId;
    originX = event.clientX;
    originY = event.clientY;
    vertical = isDesktopGallery();
    originScroll = vertical ? rail.scrollTop : rail.scrollLeft;
    dragging = false;
  });

  rail.addEventListener('pointermove', (event) => {
    if (event.pointerId !== pointerId) return;
    const dx = event.clientX - originX;
    const dy = event.clientY - originY;
    if (!dragging) {
      if (Math.hypot(dx, dy) < GALLERY_DRAG_THRESHOLD) return;
      dragging = true;
      rail.classList.add('is-dragging');
      rail.setPointerCapture(pointerId);
    }
    if (vertical) rail.scrollTop = originScroll - dy;
    else rail.scrollLeft = originScroll - dx;
  });

  const endDrag = (event) => {
    if (event.pointerId !== pointerId) return;
    const wasDragging = dragging;
    pointerId = null;
    dragging = false;
    rail.classList.remove('is-dragging');

    if (wasDragging) {
      rail.dataset.gmDragged = 'true';
      return;
    }

    const thumb = event.target instanceof Element ? event.target.closest('[data-gm-thumb]') : null;
    if (!thumb) return;
    rail.dataset.gmHandled = 'true';
    selectHeroThumb(thumb);
  };

  rail.addEventListener('pointerup', endDrag);
  rail.addEventListener('pointercancel', endDrag);
}

/**
 * Drag or swipe the main image to move to the previous or next product photo.
 * @param {Element} gallery
 * @param {HTMLElement} stage
 */
function bindStageSwipe(gallery, stage) {
  let pointerId = null;
  let originX = 0;
  let originY = 0;
  let dragging = false;

  stage.addEventListener('dragstart', (event) => event.preventDefault());

  stage.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return;
    pointerId = event.pointerId;
    originX = event.clientX;
    originY = event.clientY;
    dragging = false;
    stage.setPointerCapture(pointerId);
  });

  stage.addEventListener('pointermove', (event) => {
    if (event.pointerId !== pointerId) return;
    const dx = event.clientX - originX;
    const dy = event.clientY - originY;
    if (Math.abs(dx) < GALLERY_DRAG_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    dragging = true;
    stage.classList.add('is-dragging');
  });

  const endSwipe = (event) => {
    if (event.pointerId !== pointerId) return;
    pointerId = null;
    stage.classList.remove('is-dragging');
    if (!dragging) return;
    dragging = false;
    const dx = event.clientX - originX;
    if (Math.abs(dx) < GALLERY_SWIPE_THRESHOLD) return;
    activateGallerySlide(gallery, currentGalleryIndex(gallery) + (dx < 0 ? 1 : -1));
  };

  stage.addEventListener('pointerup', endSwipe);
  stage.addEventListener('pointercancel', endSwipe);
}

function bindGallery(gallery) {
  if (boundGalleries.has(gallery)) return;
  boundGalleries.add(gallery);

  const rail = gallery.querySelector('[data-gm-thumbs]');
  const stage = gallery.querySelector('[data-gm-stage]');
  if (rail instanceof HTMLElement) bindThumbRailDrag(rail);
  if (stage instanceof HTMLElement) bindStageSwipe(gallery, stage);
}

function bindGalleries(root = document) {
  const scope = root instanceof Element ? root : document;
  for (const gallery of scope.querySelectorAll('[data-gm-gallery]')) {
    bindGallery(gallery);
  }
}

document.addEventListener('click', (event) => {
  const thumb = event.target instanceof Element ? event.target.closest('[data-gm-thumb]') : null;
  if (!thumb) return;

  const gallery = thumb.closest('[data-gm-gallery]');
  if (!gallery) return;

  const rail = gallery.querySelector('[data-gm-thumbs]');
  if (rail instanceof HTMLElement && (rail.dataset.gmHandled || rail.dataset.gmDragged)) {
    delete rail.dataset.gmHandled;
    delete rail.dataset.gmDragged;
    return;
  }

  selectHeroThumb(thumb);
});

/* ---------- Videos ---------- */

function pauseOtherVideos(current) {
  for (const video of document.querySelectorAll('[data-gm-video] video')) {
    if (video !== current && !video.paused) video.pause();
  }
}

document.addEventListener('click', (event) => {
  const play = event.target instanceof Element ? event.target.closest('[data-gm-play]') : null;
  if (!play) return;

  const wrapper = play.closest('[data-gm-video]');
  const video = wrapper?.querySelector('video');
  if (!wrapper || !video) return;

  pauseOtherVideos(video);
  wrapper.classList.add('is-playing');
  video.controls = true;
  video.play().catch(() => {
    wrapper.classList.remove('is-playing');
    video.controls = false;
  });
  video.focus({ preventScroll: true });
});

/* ---------- Carousels ---------- */

document.addEventListener('click', (event) => {
  const arrow = event.target instanceof Element ? event.target.closest('[data-gm-prev], [data-gm-next]') : null;
  if (!arrow) return;

  const track = arrow.closest('[data-gm-carousel]')?.querySelector('[data-gm-track]');
  const card = track?.firstElementChild;
  if (!track || !card) return;

  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  const step = card.getBoundingClientRect().width + gap;
  const direction = arrow.hasAttribute('data-gm-prev') ? -1 : 1;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  track.scrollBy({ left: direction * step, behavior: reduce ? 'auto' : 'smooth' });
});

/* ---------- Tabs ---------- */

function selectTab(tab) {
  const tabs = tab.closest('[data-gm-tabs]');
  if (!tabs) return;

  for (const other of tabs.querySelectorAll('[data-gm-tab]')) {
    const selected = other === tab;
    other.setAttribute('aria-selected', String(selected));
    other.tabIndex = selected ? 0 : -1;
    const panel = document.getElementById(other.getAttribute('aria-controls') ?? '');
    if (panel) panel.hidden = !selected;
  }
}

document.addEventListener('click', (event) => {
  const tab = event.target instanceof Element ? event.target.closest('[data-gm-tab]') : null;
  if (tab) selectTab(tab);
});

document.addEventListener('keydown', (event) => {
  const tab = event.target instanceof Element ? event.target.closest('[data-gm-tab]') : null;
  if (!tab || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return;

  const all = [...(tab.closest('[role="tablist"]')?.querySelectorAll('[data-gm-tab]') ?? [])];
  const index = all.indexOf(tab);
  const next = all[(index + (event.key === 'ArrowRight' ? 1 : -1) + all.length) % all.length];
  if (next instanceof HTMLElement) {
    event.preventDefault();
    selectTab(next);
    next.focus();
  }
});

/* ---------- Dialogs ---------- */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const opener = target.closest('[data-gm-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-gm-open') ?? '');
    if (dialog instanceof HTMLDialogElement) dialog.showModal();
    return;
  }

  const dialog = target.closest('.gm-dialog');
  if (!(dialog instanceof HTMLDialogElement)) return;
  // Close on the close button, or on a click on the backdrop (the dialog itself).
  if (target.closest('[data-gm-close]') || target === dialog) dialog.close();
});

/* ---------- Reveal on scroll (workout log bars) ---------- */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              revealObserver?.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.3 }
      )
    : null;

function watchReveals(root = document) {
  for (const el of root.querySelectorAll('[data-gm-reveal]:not(.is-visible)')) {
    if (revealObserver) revealObserver.observe(el);
    else el.classList.add('is-visible');
  }
}

/* ---------- Start up, and again when the theme editor re-renders a section ---------- */

function init(root = document) {
  syncBundle();
  watchReveals(root);
  bindGalleries(root);
}

init();
document.addEventListener('shopify:section:load', (event) => init(/** @type {HTMLElement} */ (event.target)));
document.addEventListener('shopify:block:select', syncBundle);
